import { Button, Snackbar } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { dismissScaffoldUndo, useScaffoldUndo } from './scaffold-undo-store.js'

export interface ScaffoldUndoSnackbarProps {
  /** Renders only discards of this element type. */
  elementType: string
}

/**
 * The undo affordance for a discarded sketch, mounted from a plugin's `components.Top` so it
 * outlives the block it refers to.
 *
 * 8000 ms rather than the 10000 ms default: a discard decision resolves fast, and the snackbar
 * sits over the document the user is trying to look at.
 *
 * `Snackbar` already carries `role="status" aria-live="polite"`, so the discard and the restore
 * are announced without any new primitive.
 */
export const ScaffoldUndoSnackbar: FC<ScaffoldUndoSnackbarProps> = props => {
  const { elementType } = props
  const entry = useScaffoldUndo(elementType)

  if (!entry) {
    return null
  }

  const { undo } = entry

  /**
   * Dismiss first, then restore: the restore callback owns the follow-up copy ("Sketch
   * restored.") and posts it through this same store, so clearing afterwards would swallow it.
   */
  const handleUndo = () => {
    dismissScaffoldUndo()
    undo?.()
  }

  return (
    <Snackbar
      open={true}
      message={entry.message}
      autoHideDuration={8000}
      onClose={dismissScaffoldUndo}
      action={
        undo ? (
          <Button size="small" onClick={handleUndo}>
            Undo
          </Button>
        ) : null
      }
    />
  )
}

export default ScaffoldUndoSnackbar
